/* eslint eslint-comments/no-use: off */
/* eslint-disable relay/unused-fields */

import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {
  BranchPickerBase,
  type BranchPickerBaseProps,
  type BranchPickerBranch as BranchPickerBaseBranch,
} from '@github-ui/item-picker-branch'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useDebounce} from '@github-ui/use-debounce'
import {useCallback, useMemo, useState} from 'react'
import {type Environment, useRelayEnvironment} from 'react-relay'
import {fetchQuery, graphql} from 'relay-runtime'

import type {BranchPickerSharedSearchBranchesQuery} from './__generated__/BranchPickerSharedSearchBranchesQuery.graphql'

export type BranchPickerBranch = BranchPickerBaseBranch & {
  __typename?: 'Ref'
}
export async function searchBranchesQuery(
  environment: Environment,
  options: {
    owner: string
    repo: string
    query: string
  },
): Promise<BranchPickerBranch[]> {
  const {owner, repo, query} = options
  const data = await fetchQuery<BranchPickerSharedSearchBranchesQuery>(environment, BranchPickerSharedSearchBranches, {
    owner,
    name: repo,
    query,
    first: 25,
  }).toPromise()

  if (!data || !data.repository || !data.repository.refs) {
    return []
  }

  return (data?.repository?.refs?.edges || [])
    .map(edge => {
      return edge?.node ?? null
    })
    .map(b => b as BranchPickerBranch)
    .filter(b => b !== null)
}

export type BranchPickerProps = Omit<BranchPickerBaseProps, 'filterItems'> & {
  repo: string
  owner: string
  branches: BranchPickerBranch[] | null
  activeBranch?: BranchPickerBranch | null
  enforceAtleastOneSelected?: boolean
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const BranchPickerSharedSearchBranches = graphql`
  query BranchPickerSharedSearchBranchesQuery($owner: String!, $name: String!, $first: Int!, $query: String) {
    repository(owner: $owner, name: $name) {
      refs(refPrefix: "refs/heads/", first: $first, query: $query) {
        edges {
          node {
            name
            id
            __typename
            target {
              oid
              id
              __typename
            }
            repository {
              id
              nameWithOwner
              defaultBranchRef {
                name
                id
                target {
                  oid
                  id
                  __typename
                }
                associatedPullRequests {
                  totalCount
                }
              }
            }
            associatedPullRequests {
              totalCount
            }
          }
        }
      }
    }
  }
`

export function BranchPicker({
  activeBranch,
  defaultBranchId,
  branches: inputBranches,
  owner,
  repo,
  onSelectionChange,
  title,
  anchorElement,
}: BranchPickerProps) {
  const [filter, setFilter] = useState('')
  const [searchResults, setSearchResults] = useState<BranchPickerBranch[] | undefined>(undefined)
  const [searchLoading, setSearchLoading] = useState<boolean>(false)
  const {addToast} = useToastContext()

  const branches = useMemo(() => {
    const repoBranches =
      (inputBranches || [])

        .filter(branch => Boolean(branch))
        .filter(branch => branch && branch.id !== activeBranch?.id) ?? []

    if (activeBranch) {
      repoBranches.unshift(activeBranch)
    }
    return repoBranches
  }, [inputBranches, activeBranch])

  const items = useMemo(() => {
    if (searchResults) {
      return searchResults
    }
    return branches
  }, [branches, searchResults])

  const environment = useRelayEnvironment()

  const fetchSearchData = useCallback(
    async (query: string) => {
      if (!query) {
        setSearchResults(undefined)
        return
      }

      setSearchLoading(true)

      try {
        const data = await searchBranchesQuery(environment, {owner, repo, query})

        setSearchResults(data)
        setSearchLoading(false)
      } catch {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: ERRORS.couldNotSearchBranches,
        })
        setSearchLoading(false)
      }
    },
    [addToast, environment, owner, repo],
  )

  const debounceFetchSearchData = useDebounce(
    (nextValue: string) => fetchSearchData(nextValue),
    VALUES.pickerDebounceTime,
  )

  const filterItems = useCallback(
    (value: string) => {
      const trimmedFilter = value.trim()
      if (filter === trimmedFilter) return

      debounceFetchSearchData(trimmedFilter)
      setFilter(value)
    },
    [debounceFetchSearchData, filter],
  )

  return (
    <BranchPickerBase
      branches={items}
      filterItems={filterItems}
      activeBranch={activeBranch}
      defaultBranchId={defaultBranchId}
      onSelectionChange={onSelectionChange}
      isLoading={searchLoading}
      anchorElement={anchorElement}
      title={title}
    />
  )
}
